import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import RichTextEditor from "../../../atoms/RichTextEditor";
import "../../../../styles/kanbans/modals/new-job-modals.css";

export type JobCreateModalProps = {
  open: boolean;
  boards?: string[];
  lists?: string[];
  initial?: {
    company?: string;
    title?: string;
    vacancyUrl?: string;
    descriptionHtml?: string;
    board?: string;
    list?: string;
  };
  onClose: () => void;
  onSave: (data: {
    company: string;
    title: string;
    vacancyUrl: string;
    descriptionHtml: string;
    board: string;
    list: string;
  }) => void;
};

export default function JobCreateModal({
  open,
  boards = ["Job Search 2025", "Side Projects", "Freelance"],
  lists = ["wishlist", "applied", "interview", "offer", "rejected", "celebrate"],
  initial,
  onClose,
  onSave,
}: JobCreateModalProps) {
  const [company, setCompany] = useState(initial?.company ?? "");
  const [title, setTitle] = useState(initial?.title ?? "");
  const [vacancyUrl, setVacancyUrl] = useState(initial?.vacancyUrl ?? "");
  const [board, setBoard] = useState(initial?.board ?? boards[0]);
  const [list, setList] = useState(initial?.list ?? lists[0]);
  const [html, setHtml] = useState(initial?.descriptionHtml ?? "");
  const [boardOpen, setBoardOpen] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const boardRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setCompany(initial?.company ?? "");
    setTitle(initial?.title ?? "");
    setVacancyUrl(initial?.vacancyUrl ?? "");
    setBoard(initial?.board ?? boards[0]);
    setList(initial?.list ?? lists[0]);
    setHtml(initial?.descriptionHtml ?? "");
  }, [open, initial, boards, lists]);

  const canSave = useMemo(
    () => company.trim() !== "" && title.trim() !== "" && !!board && !!list,
    [company, title, board, list]
  );

  const onEsc = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [open, onEsc]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (boardRef.current && !boardRef.current.contains(e.target as Node)) {
        setBoardOpen(false);
      }
      if (listRef.current && !listRef.current.contains(e.target as Node)) {
        setListOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDescriptionChange = (html: string) => {
    setHtml(html);
  };

  if (!open) return null;

  return (
    <div
      className="jcm-ov"
      ref={overlayRef}
      onMouseDown={(e) => {
        if (e.target === overlayRef.current) onClose();
      }}
    >
      <section className="jcm" role="dialog" aria-modal="true" aria-labelledby="jcm-title">
        <header className="jcm-h">
          <h2 id="jcm-title" className="jcm-title">Add Job</h2>
          <button
            type="button"
            className="jcm-close"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </header>

        <div className="jcm-b">
          <div className="jcm-f">
            <div className="jcm-frow">
              <label htmlFor="jcm-company" className="jcm-lbl">Company</label>
              <span className="jcm-req">Required</span>
            </div>
            <input
              id="jcm-company"
              className="jcm-inp"
              placeholder="Company"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
            />
          </div>

          <div className="jcm-f">
            <div className="jcm-frow">
              <label htmlFor="jcm-title-in" className="jcm-lbl">Job Title</label>
              <span className="jcm-req">Required</span>
            </div>
            <input
              id="jcm-title-in"
              className="jcm-inp"
              placeholder="Job Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="jcm-f">
            <div className="jcm-frow">
              <label htmlFor="jcm-url" className="jcm-lbl">Job URL</label>
              <span className="jcm-opt">Optional</span>
            </div>
            <input
              id="jcm-url"
              className="jcm-inp"
              type="url"
              placeholder="https://…"
              value={vacancyUrl}
              onChange={(e) => setVacancyUrl(e.target.value)}
            />
          </div>

          <div className="jcm-f">
            <div className="jcm-frow">
              <label className="jcm-lbl">Job Description</label>
              <span className="jcm-opt">Optional</span>
            </div>
            <RichTextEditor
              value={html}
              onChange={handleDescriptionChange}
              placeholder="Describe the role, responsibilities, requirements…"
              height={220}
            />
          </div>

          <div className={boards.length > 1 ? "jcm-grid2" : "jcm-f"}>
            {boards.length > 1 && (
              <div className="jcm-f">
                <div className="jcm-frow">
                  <label className="jcm-lbl">Board</label>
                  <span className="jcm-req">Required</span>
                </div>
                <div className="jcm-dropdown" ref={boardRef}>
                  <button
                    type="button"
                    className="jcm-dropdown-trigger"
                    onClick={() => setBoardOpen(!boardOpen)}
                  >
                    <span className="jcm-dropdown-value">{board}</span>
                    <span className="jcm-dropdown-arrow">▾</span>
                  </button>
                  {boardOpen && (
                    <div className="jcm-dropdown-menu">
                      {boards.map((b) => (
                        <div
                          key={b}
                          className={`jcm-dropdown-item ${board === b ? 'jcm-dropdown-item--selected' : ''}`}
                          onClick={() => {
                            setBoard(b);
                            setBoardOpen(false);
                          }}
                        >
                          {b}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="jcm-f">
              <div className="jcm-frow">
                <label className="jcm-lbl">List</label>
                <span className="jcm-req">Required</span>
              </div>
              <div className="jcm-dropdown" ref={listRef}>
                <button
                  type="button"
                  className="jcm-dropdown-trigger"
                  onClick={() => setListOpen(!listOpen)}
                >
                  <span className="jcm-dropdown-value">{list}</span>
                  <span className="jcm-dropdown-arrow">▾</span>
                </button>
                {listOpen && (
                  <div className="jcm-dropdown-menu">
                    {lists.map((v) => (
                      <div
                        key={v}
                        className={`jcm-dropdown-item ${list === v ? 'jcm-dropdown-item--selected' : ''}`}
                        onClick={() => {
                          setList(v);
                          setListOpen(false);
                        }}
                      >
                        {v}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <footer className="jcm-ftr">
          <button className="jcm-btn-g" type="button" onClick={onClose}>Discard</button>
          <button
            className={`jcm-btn-p ${!canSave ? "is-dis" : ""}`}
            type="button"
            disabled={!canSave}
            onClick={() => {
              if (!canSave) return;
              onSave({
                company: company.trim(),
                title: title.trim(),
                vacancyUrl: vacancyUrl.trim(),
                descriptionHtml: html,
                board,
                list,
              });
            }}
          >
            Create Job
          </button>
        </footer>
      </section>
    </div>
  );
}